// Continue with Google in the browser (WEB_PLAN.md §4.3): Google Identity Services renders Google's own button;
// clicking it returns a Google ID token for the console's Web OAuth client, whose `nonce` claim is the SHA-256 we
// passed. Supabase gets the token plus the raw nonce (`signInWithIdToken`), exactly like the iOS app's native
// Google sign-in, so the same Google account lands on the same Supabase user. No client secret and no redirect
// (`signInWithOAuth` isn't used: for a Web client it would need the secret).
//
// In e2e mode (env.authStubs) there is no Google: `googleStubSignIn` mints the fake server's token format.

import { AuthFlowError } from './errors';
import { loadScript } from './loadScript';
import { makeNonce } from './nonce';
import { fakeIdToken, stubAccount } from './stubIdToken';

const GSI_SRC = 'https://accounts.google.com/gsi/client';

interface GsiCredentialResponse {
  credential?: string;
}

interface GsiIdConfiguration {
  client_id: string;
  nonce: string;
  callback: (response: GsiCredentialResponse) => void;
  use_fedcm_for_prompt?: boolean;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  itp_support?: boolean;
  context?: 'signin' | 'signup' | 'use';
}

interface GsiButtonConfiguration {
  type: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
}

interface GoogleAccountsId {
  initialize: (config: GsiIdConfiguration) => void;
  renderButton: (parent: HTMLElement, options: GsiButtonConfiguration) => void;
  cancel: () => void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

export interface GoogleButtonOptions {
  clientId: string;
  /** Called with Google's ID token and the raw nonce whose hash is in it (one attempt). */
  onCredential: (idToken: string, rawNonce: string) => void;
  /** The script couldn't load, or Google answered without a token. */
  onError: (error: unknown) => void;
}

/**
 * Renders Google's "Continue with Google" button into `el`. Every attempt gets its own nonce: after each answer the
 * button is set up again with a fresh one. Returns a cleanup for unmount.
 */
export function mountGoogleButton(el: HTMLElement, options: GoogleButtonOptions): () => void {
  let disposed = false;

  const setUp = async () => {
    try {
      await loadScript(GSI_SRC);
      const nonce = await makeNonce();
      const gsi = window.google?.accounts?.id;
      if (disposed) return;
      if (!gsi) throw new Error('Google sign-in is unavailable.');
      gsi.initialize({
        client_id: options.clientId,
        nonce: nonce.sha256,
        use_fedcm_for_prompt: true,
        auto_select: false,
        itp_support: true,
        context: 'signin',
        callback: (response) => {
          if (disposed) return;
          const credential = response.credential;
          // The nonce is spent either way: set the button up again for the next attempt.
          void setUp();
          if (!credential) {
            options.onError(new AuthFlowError('failed', "Google didn't return a sign-in token. Please try again."));
            return;
          }
          options.onCredential(credential, nonce.raw);
        },
      });
      el.replaceChildren();
      gsi.renderButton(el, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
        logo_alignment: 'center',
        // GIS accepts 200–400 px.
        width: Math.max(200, Math.min(400, Math.round(el.clientWidth || 320))),
      });
    } catch (e) {
      if (!disposed) options.onError(e);
    }
  };

  void setUp();
  return () => {
    disposed = true;
    try {
      window.google?.accounts?.id?.cancel();
    } catch {
      // Nothing to cancel.
    }
    el.replaceChildren();
  };
}

/** e2e stub: the fake server's Google token for a test account, with a real nonce pair. */
export async function googleStubSignIn(): Promise<{ idToken: string; rawNonce: string }> {
  const account = stubAccount('google');
  const nonce = await makeNonce();
  return { idToken: await fakeIdToken('google', account, nonce.sha256), rawNonce: nonce.raw };
}
